/**
 * Event RSVP Hooks - Issue #318
 * 
 * React Query hooks for managing event RSVPs:
 * - Create/update RSVP status (yes/no/maybe)
 * - Fetch user's RSVP for an event
 * - Get all RSVPs for an event
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  rsvpsApi, 
  type RSVPStatus, 
  type EventRSVP, 
  type CreateRSVPRequest, 
  type RSVPResponse, 
  type RSVPListResponse 
} from '../lib/api/rsvps';

// Re-export types for convenience
export type { RSVPStatus, EventRSVP, CreateRSVPRequest, RSVPResponse, RSVPListResponse };

// Get user's RSVP for a specific event
// Issue #604 Phase 2: Add user guard to prevent guest queries
export function useUserRSVP(eventId: string, userId?: string | null) {
  return useQuery({
    queryKey: ['user-rsvp', eventId],
    queryFn: () => rsvpsApi.getUserRSVP(eventId),
    enabled: !!eventId && !!userId, // Only fire when user is logged in
    retry: false,
    meta: { authRequired: true },
  });
}

// Get all RSVPs for an event (with counts)
// Issue #604 Phase 2: Add user guard to prevent guest queries
export function useEventRSVPs(eventId: string, userId?: string | null) {
  return useQuery({
    queryKey: ['event-rsvps', eventId],
    queryFn: () => rsvpsApi.getEventRSVPs(eventId),
    enabled: !!eventId && !!userId, // Only fire when user is logged in
    retry: false,
    meta: { authRequired: true },
  });
}

// Create or update an RSVP
export function useCreateRSVP(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRSVPRequest) => rsvpsApi.createRSVP(eventId, data),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['user-rsvp', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event-rsvps', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}